// Sticky-row positioning + map-driven scroll helpers for the tree.
// Exported for direct unit testing — the pure functions take/return data and
// only the explicitly DOM-mutating ones touch the DOM.

// Returns true when a batch of MutationRecord entries contains at least one
// tree-node addition or removal. Used by both sticky-positioning effects to
// skip noise (checkbox state, etc.) that doesn't affect row layout.
export const hasStructuralChange = (mutations: MutationRecord[]) =>
  mutations.some(mutation =>
    [mutation.addedNodes, mutation.removedNodes].some(nodes =>
      Array.from(nodes).some(
        node =>
          node instanceof HTMLElement &&
          node.classList.contains('syt-antd-tree-treenode')
      )
    )
  );

// One snapshot of a sticky row's geometry at a moment in time. Produced
// by `measureStickyRows` and consumed by `computeTierTranslates`.
export interface StickyRowMeasurement {
  row: HTMLElement;
  stickyTop: number;
  height: number;
  distance: number;
}

// Snapshot the inline `top`, height, and viewport distance for each row.
// All reads happen in one pass to keep the layout flush batched.
export const measureStickyRows = (
  rows: HTMLElement[],
  containerTop: number
): StickyRowMeasurement[] =>
  rows.map(row => ({
    row,
    stickyTop: Number.parseFloat(globalThis.getComputedStyle(row).top) || 0,
    height: row.offsetHeight,
    distance: row.getBoundingClientRect().top - containerTop
  }));

// Recover each row's TRUE sticky distance by re-adding the px the scroll effect
// pushed it last frame. Rows are read without clearing their transform first,
// so a translated row measures `trueDistance - appliedOffset`; adding the
// applied offset back undoes that. Pure.
export const adjustMeasuredDistances = (
  measurements: StickyRowMeasurement[],
  appliedTranslate: Map<HTMLElement, number>
): StickyRowMeasurement[] =>
  measurements.map(m => ({
    ...m,
    distance: m.distance + (appliedTranslate.get(m.row) ?? 0)
  }));

// Given snapshots for a single tier of sticky rows (e.g. all farm rows, in DOM
// order), return the rows to shift up and by how many pixels. Two parts:
//
//   1. Earlier-pinned rows are bottom-aligned with the latest pinned row (kept
//      for completeness; the scroll listener also hides these via
//      `computeStaleRows`).
//   2. CONTINUOUS hand-off: the active (latest pinned) row is pushed up as the
//      *next, not-yet-pinned* row of the tier rises into its bottom — driven by
//      that next row's live scroll position, not by waiting for it to pin. So
//      the active header rides off in lockstep with the scroll (no wait, no
//      pop) and is fully gone exactly when the next row reaches the line. This
//      is what stops a taller wrapped field peeking over the next field's crop
//      zones mid-scroll.
//
// Pure: takes measurements, returns instructions; does not touch the DOM.
export const computeTierTranslates = (
  measurements: StickyRowMeasurement[]
): { row: HTMLElement; offsetPx: number }[] => {
  const result: { row: HTMLElement; offsetPx: number }[] = [];
  const isPinned = (m: StickyRowMeasurement) => m.distance - m.stickyTop < 1;

  let latestIdx = -1;
  measurements.forEach((m, i) => {
    if (isPinned(m)) latestIdx = i;
  });
  if (latestIdx === -1) return result;

  const latest = measurements[latestIdx];
  const latestBottom = latest.stickyTop + latest.height;

  // (1) earlier-pinned rows → bottom-align with the latest.
  measurements.slice(0, latestIdx).forEach(m => {
    if (!isPinned(m)) return;
    const offsetPx = m.stickyTop + m.height - latestBottom;
    if (offsetPx > 0) result.push({ row: m.row, offsetPx });
  });

  // (2) continuous hand-off, scoped to the *trouble* rows only: a handoff only
  // peeks when the active header is taller (wrapped) than the incoming one —
  // otherwise the incoming row simply covers it, the original behavior. So only
  // then do we ride the active row off, pushing it up by how far the next
  // not-yet-pinned row has risen into its bottom, clamped to its own height.
  const incoming = measurements[latestIdx + 1];
  if (incoming && !isPinned(incoming) && latest.height > incoming.height) {
    const overlap = latestBottom - incoming.distance;
    if (overlap > 0) {
      result.push({
        row: latest.row,
        offsetPx: Math.min(overlap, latest.height)
      });
    }
  }

  return result;
};

// Once the next same-tier header has pinned (so two are pinned at the same
// top), the earlier one is fully superseded — it should be hidden outright. The
// continuous hand-off above slides it off as the next row rises; this is the
// clean end-state so a taller wrapped header can never linger behind the active
// one. The scroll listener hides exactly these rows (and re-shows them once
// they leave the set).
export const computeStaleRows = (
  measurements: StickyRowMeasurement[]
): HTMLElement[] => {
  const pinned = measurements.filter(m => m.distance - m.stickyTop < 1);
  return pinned.slice(0, -1).map(m => m.row);
};

// Fold the per-tier translate + stale-hide instructions for one frame into the
// two diff sets the scroll effect writes to the DOM: the rows to translate (and
// by how much) and the rows to hide outright. Kept module-level (rather than
// inlined in the scroll effect) so the fan-out stays shallow and unit-testable.
// Pure.
export const computeDesiredState = (
  measurementsByTier: StickyRowMeasurement[][]
): { translate: Map<HTMLElement, number>; hidden: Set<HTMLElement> } => {
  const translate = new Map<HTMLElement, number>();
  const hidden = new Set<HTMLElement>();
  measurementsByTier.forEach(measurements => {
    computeTierTranslates(measurements).forEach(({ row, offsetPx }) => {
      translate.set(row, offsetPx);
    });
    computeStaleRows(measurements).forEach(row => hidden.add(row));
  });
  return { translate, hidden };
};

// Hide-side hysteresis for the stale-row pass. Hiding a frame late is
// invisible — the active header paints over the stale row (later DOM order),
// which part (1) of computeTierTranslates keeps bottom-aligned. Showing a
// frame late is a visible hole during fast upward scrolls: the row that
// should now be the active header is still hidden from the previous frame.
// So a hide is only confirmed once a row has stayed stale for `threshold`
// consecutive frames, while a row leaving the stale set re-shows instantly.
// `staleStreak` is the caller's persistent per-row streak ledger; it is
// updated in place each frame (rows no longer stale are pruned).
export const computeConfirmedHidden = (
  desiredHidden: Set<HTMLElement>,
  staleStreak: Map<HTMLElement, number>,
  threshold: number
): Set<HTMLElement> => {
  staleStreak.forEach((_streak, row) => {
    if (!desiredHidden.has(row)) staleStreak.delete(row);
  });
  const confirmed = new Set<HTMLElement>();
  desiredHidden.forEach(row => {
    const streak = (staleStreak.get(row) ?? 0) + 1;
    staleStreak.set(row, streak);
    if (streak >= threshold) confirmed.add(row);
  });
  return confirmed;
};

// Walks up from `el`'s parent to the nearest ancestor that actually scrolls
// (computed overflow/overflow-y of auto or scroll), or null when none does.
// Single source of truth for "which container scrolls the tree" — used by both
// the sticky scroll effect and the map-driven scroll below, which must agree.
// Note: starts at the PARENT, so `el` itself is never returned.
export const findScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
  let ancestor: HTMLElement | null = el.parentElement;
  while (ancestor) {
    const { overflow, overflowY } = globalThis.getComputedStyle(ancestor);
    if (/(auto|scroll)/.test(overflow + overflowY)) break;
    ancestor = ancestor.parentElement;
  }
  return ancestor;
};

// Scrolls the field row (`fieldId`) so it sits just below the pinned headers.
// Returns false if the row isn't in the DOM yet so the caller can retry while
// the expanded branch mounts.
//
// Rows are position:sticky, so a pinned row reports its pinned position, not
// its real one (this is why scroll-up missed). We briefly drop the sticky to
// measure the true offset, then restore it — same task, no paint, no flicker.
//
// The field's own sticky `top` is the combined height of the headers that pin
// above it (farm, and crop group in crop mode). We land the field at that
// offset — NOT at the very top — otherwise sticky re-pins it lower and its
// first crop zones end up hidden behind the pinned farm/crop-group headers.
export const scrollFieldNodeToTop = (
  container: HTMLDivElement,
  fieldId: string
): boolean => {
  const el = container.querySelector(
    `[data-field-id="${CSS.escape(fieldId)}"]`
  ) as HTMLElement | null;
  const treenode = el?.closest('.syt-antd-tree-treenode') as HTMLElement | null;
  if (!treenode) return false;

  // The Tree has no height prop so .syt-antd-tree-list-holder is NOT
  // scrollable — it expands to full height. Walk up to the real scroll container.
  const scrollContainer = findScrollableAncestor(treenode);
  if (!scrollContainer) return false;

  // Read the sticky offset (height of the headers that pin above this field)
  // while it is still pinned, before we drop the sticky to measure.
  const stickyTop =
    Number.parseFloat(globalThis.getComputedStyle(treenode).top) || 0;

  const prevPosition = treenode.style.position;
  treenode.style.position = 'static'; // unpin to read the true position
  const delta =
    treenode.getBoundingClientRect().top -
    scrollContainer.getBoundingClientRect().top;
  treenode.style.position = prevPosition; // re-pin before the scroll runs

  scrollContainer.scrollTo({
    // Subtract stickyTop so the field lands at its pin line (below the pinned
    // headers) instead of the very top — keeps its crop zones from being hidden.
    top: scrollContainer.scrollTop + delta - stickyTop,
    behavior: 'smooth'
  });
  return true;
};
